import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

export const FamilyPhysicianDoctors: React.FC = () => {
  const doctors = [
    {
      name: "Dr. Sarah Johnson",
      title: "Family Physician",
      image: "https://images.pexels.com/photos/5452201/pexels-photo-5452201.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
      education: [
        "M.D. University of Michigan Medical School",
        "Residency in Family Medicine, Mayo Clinic",
        "Board Certified in Family Medicine"
      ],
      specialties: ["Preventive Care", "Women's Health", "Geriatrics"],
      bio: "Dr. Johnson has been practicing family medicine for over 15 years. She is dedicated to providing comprehensive care for patients of all ages and building long-lasting relationships with families in our community. Her approach to medicine emphasizes preventive care, patient education, and shared decision-making.",
      philosophy: "I believe in treating the whole person, not just the illness. My goal is to partner with my patients to help them achieve optimal health through preventive care, lifestyle modifications, and appropriate medical interventions when needed.",
      languages: ["English", "Spanish"],
      accepting: true
    },
    {
      name: "Dr. Michael Chen",
      title: "Family Physician",
      image: "https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
      education: [
        "M.D. Johns Hopkins University School of Medicine",
        "Residency in Family Medicine, University of California San Francisco",
        "Fellowship in Sports Medicine, Stanford University",
        "Board Certified in Family Medicine and Sports Medicine"
      ],
      specialties: ["Pediatrics", "Sports Medicine", "Chronic Disease Management"],
      bio: "Dr. Chen brings a wealth of experience in family medicine with special interests in pediatric care and sports medicine. He believes in a holistic approach to healthcare and empowering patients through education. Dr. Chen is particularly skilled in treating sports injuries and helping athletes of all levels perform at their best while staying healthy.",
      philosophy: "I strive to provide evidence-based care that considers each patient's unique circumstances and preferences. I believe that good health comes from a balance of physical activity, proper nutrition, stress management, and preventive healthcare.",
      languages: ["English", "Mandarin"],
      accepting: true
    },
    {
      name: "Dr. Emily Rodriguez",
      title: "Family Physician",
      image: "https://images.pexels.com/photos/5214958/pexels-photo-5214958.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
      education: [
        "M.D. Northwestern University Feinberg School of Medicine",
        "Residency in Family Medicine, University of Washington",
        "Board Certified in Family Medicine"
      ],
      specialties: ["Preventive Care", "Mental Health", "Diabetes Management"],
      bio: "Dr. Rodriguez is passionate about preventive care and helping patients manage chronic conditions. She takes time to listen to her patients and develop personalized treatment plans that address their unique needs. With special training in mental health care, Dr. Rodriguez provides compassionate support for patients dealing with anxiety, depression, and other mental health concerns.",
      philosophy: "I believe that mental and physical health are deeply connected, and both are essential for overall wellbeing. My approach focuses on preventive care, early intervention, and creating a supportive environment where patients feel comfortable discussing all aspects of their health.",
      languages: ["English", "Spanish"],
      accepting: true
    }
  ];

  const staff = [
    {
      name: "Jennifer Martinez, RN",
      title: "Nurse Manager",
      image: "https://images.pexels.com/photos/5407206/pexels-photo-5407206.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
      bio: "Jennifer has been with our practice for 10 years and oversees our nursing staff. She is dedicated to ensuring that all patients receive exceptional care."
    },
    {
      name: "Robert Thompson",
      title: "Practice Administrator",
      image: "https://images.pexels.com/photos/5393594/pexels-photo-5393594.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
      bio: "Robert manages the day-to-day operations of our practice, ensuring that everything runs smoothly so our medical team can focus on patient care."
    },
    {
      name: "Lisa Chen, PA-C",
      title: "Physician Assistant",
      image: "https://images.pexels.com/photos/5327656/pexels-photo-5327656.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
      bio: "Lisa works closely with our physicians to provide comprehensive care to patients of all ages. She has special interests in preventive care and women's health."
    },
    {
      name: "David Garcia",
      title: "Medical Assistant",
      image: "https://images.pexels.com/photos/6749773/pexels-photo-6749773.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
      bio: "David assists our physicians with patient care and procedures. He is known for his gentle approach and ability to put patients at ease."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Meet Our Doctors</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Our team of experienced physicians is dedicated to providing the highest quality care for you and your family.
            </p>
          </div>
        </div>
      </section>

      {/* Doctors Profiles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Physicians</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get to know the dedicated doctors who will be caring for you and your family.
            </p>
          </div>
          
          <div className="space-y-16">
            {doctors.map((doctor, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-3">
                  <div className="lg:col-span-1 p-6 flex flex-col items-center lg:border-r border-gray-200">
                    <img 
                      src={doctor.image} 
                      alt={doctor.name} 
                      className="w-48 h-48 rounded-full object-cover mb-6"
                    />
                    <h3 className="text-2xl font-bold text-gray-900 text-center">{doctor.name}</h3>
                    <p className="text-primary-600 font-medium text-center mb-4">{doctor.title}</p>
                    
                    <div className="w-full mt-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Specialties:</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {doctor.specialties.map((specialty, specIndex) => (
                          <span key={specIndex} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="w-full mt-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Languages:</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {doctor.languages.map((language, langIndex) => (
                          <span key={langIndex} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                            {language}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="w-full mt-6 text-center">
                      {doctor.accepting ? (
                        <div className="text-green-600 font-medium flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Accepting New Patients
                        </div>
                      ) : (
                        <div className="text-gray-500 font-medium">
                          Not Currently Accepting New Patients
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="lg:col-span-2 p-6">
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">About:</h4>
                      <p className="text-gray-600 leading-relaxed">{doctor.bio}</p>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Philosophy of Care:</h4>
                      <p className="text-gray-600 leading-relaxed italic">{doctor.philosophy}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Education & Credentials:</h4>
                      <ul className="space-y-2">
                        {doctor.education.map((edu, eduIndex) => (
                          <li key={eduIndex} className="flex items-center text-gray-600">
                            <CheckCircle className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                            {edu}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {doctor.accepting && (
                      <div className="mt-8">
                        <Link to="/templates/family-physician#appointment" className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-block">
                          Schedule with Dr. {doctor.name.split(' ')[1]}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Staff */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Staff</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the dedicated team members who support our physicians and help provide exceptional care.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {staff.map((person, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                <img 
                  src={person.image} 
                  alt={person.name} 
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-gray-900">{person.name}</h3>
                <p className="text-primary-600 font-medium mb-4">{person.title}</p>
                <p className="text-gray-600">{person.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Schedule Your Visit?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Our doctors are accepting new patients and would be honored to care for you and your family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/templates/family-physician#appointment" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Book an Appointment
            </Link>
            <Link to="/templates/family-physician#contact" className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};